import React, { useEffect, useState } from "react";
import { Badge, Button, Dropdown, Form, Modal, Table } from "react-bootstrap";
import PageTitle from "../../../layouts/PageTitle";
import Toast, { notifyTopRight, notifyError } from "../../toast/SetToast";
import { createSalePackage, updateSalePackage, useSalePackages } from "./salePackageCatalog";

const PAGE_SIZE = 25;

const SalePackageList = () => {
  const { packages, isLoading, error, retry } = useSalePackages();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [sort, setSort] = useState("asc");
  const [page, setPage] = useState(1);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [packageId, setPackageId] = useState("");
  const [name, setName] = useState("");
  const [formStatus, setFormStatus] = useState("Active");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const filtered = packages.filter((item) =>
    [item.name, item.packageId || item.id].some((value) =>
      String(value || "").toLowerCase().includes(search.trim().toLowerCase())) &&
    (status === "All" || item.IsActive === status));
  if (sort === "desc") filtered.reverse();
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, status, sort]);
  useEffect(() => { setPage((value) => Math.min(value, totalPages)); }, [totalPages]);

  const openForm = (item = null) => {
    setEditing(item);
    setPackageId(item ? item.packageId || item.id : "");
    setName(item ? item.name : "");
    setFormStatus(item ? item.IsActive : "Active");
    setSaveError("");
    setShow(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveError("");
    try {
      if (editing) await updateSalePackage(editing.id, formStatus, packageId);
      else await createSalePackage(name, formStatus, packageId);
      notifyTopRight("บันทึกข้อมูลสำเร็จ");
      setShow(false);
    } catch (failure) {
      setSaveError(failure.message || "Unable to save sale package.");
      notifyError(failure.message || "Unable to save sale package.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Toast />
      <PageTitle activeMenu="Sale Package List" motherMenu="Items" />
      <div className="form-head d-flex flex-wrap flex-lg-nowrap mb-3 mb-lg-5 align-items-center">
        <Button onClick={() => openForm()} disabled={isLoading || Boolean(error)}>Add Sale Package</Button>
        <div className="input-group search-area ml-auto d-inline-flex" style={{ flex: "1 1 200px", minWidth: 180 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search Sale Package"
            aria-label="Search package name or ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="input-group-append">
            <span className="input-group-text">
              <i className="flaticon-381-search-2" aria-hidden="true"></i>
            </span>
          </div>
        </div>
        <Dropdown className="align-items-end d-inline-flex ml-2" onSelect={(option) => setSort(option)}>
          <Dropdown.Toggle variant="outline-primary" id="package-sort" aria-label="Sort name">
            <i className="flaticon-381-controls-3" aria-hidden="true"></i> by {sort === "asc" ? "A-Z" : "Z-A"}
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu-right">
            <Dropdown.Item eventKey="asc">A-Z</Dropdown.Item>
            <Dropdown.Item eventKey="desc">Z-A</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        <Dropdown className="align-items-end d-inline-flex ml-2" onSelect={(option) => setStatus(option)}>
          <Dropdown.Toggle variant="outline-primary" id="package-filter" aria-label="Status filter">
            <i className="flaticon-381-menu-1" aria-hidden="true"></i> {status === "All" ? "Status All" : status}
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu-right">
            {["All", "Active", "Inactive"].map((value) => (
              <Dropdown.Item key={value} eventKey={value}>{value}</Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Sale Package List</h4>
        </div>
        <div className="card-body">
          {isLoading && <p role="status">Loading sale packages...</p>}
          {error && <div className="alert alert-danger" role="alert">{error} <Button size="sm" onClick={retry}>Retry</Button></div>}
          {!isLoading && !error && <>
            <Table responsive bordered size="sm">
              <thead><tr><th>Package ID</th><th>Package Name</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {rows.map((item) => <tr key={item.id}>
                  <td style={{ overflowWrap: "anywhere" }}>{item.packageId || item.id}</td>
                  <td>{item.name}</td>
                  <td><Badge variant={item.IsActive === "Active" ? "success" : "secondary"}>{item.IsActive}</Badge></td>
                  <td><Button size="sm" variant="outline-primary" onClick={() => openForm(item)}>Edit</Button></td>
                </tr>)}
                {!rows.length && <tr><td colSpan={4}>No sale packages found.</td></tr>}
              </tbody>
            </Table>
            <div className="d-flex align-items-center justify-content-between">
              <span>{filtered.length} packages · Page {currentPage} of {totalPages}</span>
              <div>
                <Button size="sm" variant="light" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</Button>
                <Button size="sm" variant="light" className="ml-2" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>Next</Button>
              </div>
            </div>
          </>}
        </div>
      </div>
      <Modal show={show} onHide={() => { if (!saving) setShow(false); }} centered>
        <Modal.Header closeButton={!saving}><Modal.Title>{editing ? "Edit Sale Package" : "Add Sale Package"}</Modal.Title></Modal.Header>
        <Form onSubmit={save}>
          <Modal.Body>
            <Form.Group controlId="package-id">
              <Form.Label>Package ID</Form.Label>
              <Form.Control value={packageId} required disabled={saving} onChange={(event) => setPackageId(event.target.value)} placeholder="Enter package ID" />
            </Form.Group>
            <Form.Group controlId="package-name">
              <Form.Label>Package Name</Form.Label>
              <Form.Control value={name} required disabled={Boolean(editing) || saving} onChange={(event) => setName(event.target.value)} />
              <Form.Text>Names cannot be changed after creation. Deactivate an old package and add a replacement when needed.</Form.Text>
            </Form.Group>
            <Form.Group controlId="package-status">
              <Form.Label>Status</Form.Label>
              <Form.Control as="select" value={formStatus} disabled={saving} onChange={(event) => setFormStatus(event.target.value)}>
                <option>Active</option><option>Inactive</option>
              </Form.Control>
            </Form.Group>
            {saveError && <div className="alert alert-danger" role="alert">{saveError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" disabled={saving} onClick={() => setShow(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SalePackageList;
